// src/contexts/AdminContext.js
// 관리자 상태를 앱 전체에서 공유하는 Context
// - 서버에서 세션을 확인한 이후에만 운영진 기능을 활성화
// - 배경 검증: 마운트 시 서버에 토큰 유효성 확인
// - 쓰기 보안: getAdminClient()의 x-admin-token 헤더로 서버가 매 요청 검증
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { verifyAdminSession, clearAdminSession, hasAdminToken } from '../services/supabase';

const AdminContext = createContext({
  isAdmin: false,
  isChecking: true,
  activateAdmin: () => {},
  deactivateAdmin: () => {},
});

export const useAdmin = () => useContext(AdminContext);

export function AdminProvider({ children }) {
  // 저장된 토큰 자체는 권한의 증거가 아닙니다.
  const [isAdmin, setIsAdmin] = useState(false);
  const [isChecking, setIsChecking] = useState(() => hasAdminToken());

  // 로그인 성공 시 호출
  const activateAdmin = useCallback(() => {
    setIsAdmin(true);
    setIsChecking(false);
  }, []);

  // 로그아웃 시 호출
  const deactivateAdmin = useCallback(() => {
    clearAdminSession();
    setIsAdmin(false);
    setIsChecking(false);
  }, []);

  // 마운트 시 배경 검증 (3-상태: 'valid' | 'invalid' | 'error')
  useEffect(() => {
    if (!hasAdminToken()) {
      setIsAdmin(false);
      setIsChecking(false);
      return;
    }

    let cancelled = false;

    const verify = async () => {
      const MAX_RETRIES = 3;
      let lastResult = 'error';

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (cancelled) return;

        lastResult = await verifyAdminSession();

        if (lastResult === 'valid') {
          if (!cancelled) {
            setIsAdmin(true);
            setIsChecking(false);
          }
          return;
        }

        if (lastResult === 'invalid') {
          if (!cancelled) {
            clearAdminSession();
            setIsAdmin(false);
            setIsChecking(false);
          }
          return;
        }

        // 'error' — retry with backoff
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }

      // Keep the token for retry, but never enable management without verification.
      if (!cancelled) {
        setIsAdmin(false);
        setIsChecking(false);
      }
    };

    verify();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminContext.Provider value={{ isAdmin, isChecking, activateAdmin, deactivateAdmin }}>
      {children}
    </AdminContext.Provider>
  );
}
